import { ApiError, type DocumentOcrDraft, type DocumentType, type IntakeDocument } from "./api.js";

export interface DocumentIntakeApi {
  upload(input: { docType: DocumentType; mimeType: string; imageBase64: string; originalFilename: string; clientRequestId: string }): Promise<void>;
  openImage(id: string): Promise<{ mimeType: string; imageBase64: string }>;
  list(): Promise<readonly IntakeDocument[]>;
  runOcr?(id:string): Promise<{draft:DocumentOcrDraft;version:number}>;
  skipOcr?(id:string): Promise<{draft:DocumentOcrDraft;version:number}>;
  saveDraft?(id:string,version:number,draft:DocumentOcrDraft): Promise<{draft:DocumentOcrDraft;version:number}>;
  registrationOptions?(): Promise<{vendors:Array<{id:string;name:string;category:string}>;accounts:Array<{id:string;name:string}>}>;
  register?(id:string,kind:"purchase"|"petty-cash"|"sales",payload:Record<string,unknown>):Promise<{registration:{target:string;id:string;duplicate:boolean}}>;
  /**
   * 登録先の画面へ移動する（引き継ぎ §8.4）。
   *
   * ★訂正は**登録先の正規経路**で行う。文書を未登録へ戻して登録し直す道は作らない
   *   （二重計上と履歴の消失を起こす）。
   */
  openRegistered?(target:string,recordId:string|null):void;
}

export interface DocumentCameraOptions {
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  canApprovePurchase?: boolean;
}

let unsavedDrafts = 0;
interface EditorState { draft:DocumentOcrDraft; version:number; dirty:boolean; saving:boolean }
const editorStates=new Map<string,EditorState>();
export const hasUnsavedDocumentDrafts = (): boolean => unsavedDrafts > 0 || Array.from(editorStates.values()).some((state)=>state.dirty || state.saving);
export function confirmDiscardDocumentDrafts(confirmFn: (message: string) => boolean = window.confirm): boolean {
  if (!hasUnsavedDocumentDrafts()) return true;
  if (!confirmFn("保存していない画像または確認下書きがあります。破棄して移動しますか？")) return false;
  unsavedDrafts = 0; editorStates.clear();
  return true;
}
// ★リロード・タブを閉じるときの確認は `main.ts` の `onBeforeUnload` が持つ（判定を1箇所に）

const LABELS: Record<DocumentType, string> = {
  DELIVERY_NOTE: "納品書",
  RECEIPT: "買い出し領収書",
  POS_CLOSE: "Airレジ締めレシート",
};
const STATUS: Record<IntakeDocument["workflow_status"], string> = {
  CAPTURED: "● 撮影済み",
  READING: "… 読取中",
  NEEDS_REVIEW: "! 要確認",
  REGISTERED: "✓ 登録済み",
  FAILED: "× 失敗",
};

const node = <K extends keyof HTMLElementTagNameMap>(tag: K, text?: string) => {
  const n = document.createElement(tag);
  if (text !== undefined) n.textContent = text;
  return n;
};

/** 登録先のテーブル名 → 画面の呼び名。★テーブル名をそのまま利用者に見せない */
const TARGET_LABEL: Record<string, string> = { purchases: "仕入", petty_cash_entries: "小口現金", daily_sales: "売上" };

/**
 * ★**区分・支払方法は `src/ui/main.ts` と同じ文言**（`CATEGORY_LABEL` / `METHOD_LABEL`）。
 *   向こうは `main.ts` の内部定数で、この画面は `main.ts` から読み込まれる側なので
 *   共有すると読み込みが環になる。**値（FOOD/CASH…）の唯一の根拠は
 *   `src/infra/purchases.ts` / `src/infra/pettyCash.ts`** で、ここは表示名だけを持つ。
 */
const CATEGORY_LABEL: Record<string, string> = { FOOD: "フード", DRINK: "ドリンク", OTHER: "その他" };
const METHOD_LABEL: Record<string, string> = { CASH: "現金", CREDIT: "クレジット", OTHER: "その他" };

/** ラベル付きの選択欄。★`prompt` で ID・英語コードを打たせない（docs/10・引き継ぎ §3.3） */
const labeledSelect = (labelText: string, options: ReadonlyArray<{ value: string; label: string }>): { label: HTMLLabelElement; select: HTMLSelectElement } => {
  const label = node("label", labelText);
  const select = node("select");
  for (const option of options) { const o = node("option", option.label); o.value = option.value; select.append(o); }
  label.append(select);
  return { label, select };
};

const base64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(reader.error);
  reader.onload = () => resolve(String(reader.result).split(",", 2)[1] ?? "");
  reader.readAsDataURL(blob);
});

export function canvasPoint(
  canvas: Pick<HTMLCanvasElement, "width" | "height">,
  rect: Pick<DOMRect, "left" | "top" | "width" | "height">,
  client: { clientX: number; clientY: number },
): { x: number; y: number } {
  return {
    x: (client.clientX - rect.left) * canvas.width / rect.width,
    y: (client.clientY - rect.top) * canvas.height / rect.height,
  };
}

export function documentIntakeView(documents: readonly IntakeDocument[], api: DocumentIntakeApi, cameraOptions: DocumentCameraOptions = {}): HTMLElement {
  unsavedDrafts = 0;
  const visibleIds=new Set(documents.map((document)=>document.id));
  for(const [id,state] of editorStates){if(!state.dirty&&!state.saving){const latest=documents.find((document)=>document.id===id);if(!visibleIds.has(id)||latest?.ocr_draft_version!==state.version) editorStates.delete(id);}}
  let selectedDocumentId: string | null = null;
  const section = node("section");
  section.className = "document-intake";
  section.append(node("h2", "伝票を撮る"), node("p", "撮る → 確認待ち。撮影しただけでは売上・仕入・小口へ登録されません。"));

  const picker = node("div");
  picker.className = "document-picker";
  const type = node("select");
  type.setAttribute("aria-label", "伝票の種類");
  (Object.keys(LABELS) as DocumentType[]).forEach((value) => {
    const option = node("option", LABELS[value]); option.value = value; type.append(option);
  });
  const input = node("input");
  input.type = "file"; input.accept = "image/jpeg,image/png,image/webp"; input.multiple = true;
  input.setAttribute("capture", "environment"); input.setAttribute("aria-label", "写真を撮る、または選ぶ");
  const inputLabel = node("label", "端末にある写真を選ぶ（補助）");
  inputLabel.append(input);
  picker.append(type);

  const cameraStart = node("button", "撮影を始める"); cameraStart.type="button"; cameraStart.className="camera-launch";
  const cameraPanel = node("div"); cameraPanel.className="document-camera"; cameraPanel.hidden=true;
  const video = node("video"); video.autoplay=true; video.playsInline=true; video.muted=true;
  const cameraMessage = node("p"); cameraMessage.setAttribute("role", "status");
  const shutter = node("button", "シャッター"); shutter.type="button";
  const captured = node("canvas"); captured.hidden=true;
  const retake = node("button", "撮り直す"); retake.type="button"; retake.hidden=true;
  const adopt = node("button", "この写真を使う"); adopt.type="button"; adopt.hidden=true;
  const closeCamera = node("button", "撮影を閉じる"); closeCamera.type="button";
  cameraPanel.append(video, captured, cameraMessage, shutter, retake, adopt, closeCamera);
  picker.append(cameraStart, inputLabel, cameraPanel);
  section.append(picker);

  const drafts = node("div"); section.append(drafts);
  const addDraft = (file: File, selectedType: DocumentType) => {
      unsavedDrafts += 1;
      const clientRequestId = crypto.randomUUID().replaceAll("-", "");
      const card = node("article"); card.className = "document-draft";
      card.append(node("h3", file.name), node("p", "黒く塗る操作で従業員氏名を隠し、実際に送る画像を確認してください。"));
      const canvas = node("canvas"); const ctx = canvas.getContext("2d");
      let loaded = false;
      let uploading = false;
      const img = new Image(); img.onload = () => {
        const scale = Math.min(1, 8192 / Math.max(img.naturalWidth, img.naturalHeight));
        canvas.width = Math.round(img.naturalWidth * scale); canvas.height = Math.round(img.naturalHeight * scale);
        if (canvas.width < 320 || canvas.height < 320) {
          card.append(node("p", "画像の短い辺が320px未満です。解像度を上げて撮り直してください。"));
          URL.revokeObjectURL(img.src);
          return;
        }
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        loaded = true;
        send.disabled = !confirmed.checked;
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => { URL.revokeObjectURL(img.src); card.append(node("p", "画像を開けません。別の写真を選んでください。")); };
      img.src = URL.createObjectURL(file);
      let drawing = false;
      const point = (e: PointerEvent) => canvasPoint(canvas, canvas.getBoundingClientRect(), e);
      canvas.addEventListener("pointerdown", (e) => { if (uploading) return; drawing=true; canvas.setPointerCapture(e.pointerId); const p=point(e); ctx?.beginPath(); ctx?.moveTo(p.x,p.y); });
      canvas.addEventListener("pointermove", (e) => { if (uploading || !drawing || !ctx) return; const p=point(e); ctx.strokeStyle="#000"; ctx.lineWidth=Math.max(18,canvas.width/35); ctx.lineCap="round"; ctx.lineTo(p.x,p.y); ctx.stroke(); });
      canvas.addEventListener("pointerup", () => { drawing=false; });
      const label = node("label"); label.className = "checkbox"; const confirmed = node("input"); confirmed.type="checkbox";
      label.append(confirmed, document.createTextNode(" 氏名を隠した送信画像を確認しました"));
      const send = node("button", "確認待ちへ保存"); send.type="button"; send.disabled=true;
      confirmed.addEventListener("change", () => { send.disabled=!confirmed.checked || !loaded; });
      send.addEventListener("click", async () => {
        if (uploading) return;
        uploading=true; send.disabled=true; confirmed.disabled=true; input.disabled=true; type.disabled=true;
        try {
          const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, file.type === "image/png" ? "image/png" : "image/jpeg", 0.86));
          if (blob === null) throw new Error("画像を作れませんでした");
          await api.upload({ docType: selectedType, mimeType: blob.type, imageBase64: await base64(blob), originalFilename: file.name, clientRequestId });
          card.replaceChildren(node("p", "✓ 確認待ちへ保存しました"));
          unsavedDrafts = Math.max(0, unsavedDrafts - 1);
          try {
            const updated=await api.list();
            // ★保存した伝票が並ぶ確認待ちへ寄せる。カードが1行に縮んで、次に押すものが画面の外へ行くため
            if (!hasUnsavedDocumentDrafts()) { renderQueue(updated); queue.scrollIntoView?.({ block: "start" }); }
          } catch {
            const refresh = node("button", "確認待ちを更新する"); refresh.type="button";
            const error = node("p", "保存しましたが、確認待ちを更新できませんでした。"); error.setAttribute("role","alert");
            refresh.addEventListener("click", async () => {
              refresh.disabled=true;
              try { const updated=await api.list(); if (!hasUnsavedDocumentDrafts()) renderQueue(updated); } catch { refresh.disabled=false; }
            });
            queue.prepend(error, refresh);
          }
        } catch {
          const old = card.querySelector("[role=alert]"); old?.remove();
          const error = node("p", "保存できませんでした。通信状態を確認して、もう一度保存してください。"); error.setAttribute("role", "alert"); send.before(error);
          send.disabled = false;
        } finally {
          uploading=false; confirmed.disabled=false; input.disabled=false; type.disabled=false;
        }
      });
      /*
       * ★**1枚だけ捨てられるようにする**（引き継ぎ §3.2「不要・失敗した未保存写真を
       *   1枚だけ破棄できる。別写真の下書きは消さない」）。
       *   もとは「全部破棄」しか無く、失敗した1枚を捨てるために
       *   他の写真の黒塗りまでやり直すことになっていた。
       * ★送信中は捨てない（途中で消すと、保存された伝票の控えが画面から消える）。
       */
      const discard = node("button", "この写真をやめる"); discard.type="button"; discard.className="document-draft-discard";
      discard.addEventListener("click", () => {
        if (uploading) return;
        unsavedDrafts = Math.max(0, unsavedDrafts - 1);
        card.remove();
      });
      card.append(canvas,label,send,discard); drafts.append(card);
  };
  input.addEventListener("change", () => {
    const selectedType = type.value as DocumentType;
    for (const file of Array.from(input.files ?? [])) addDraft(file, selectedType);
    input.value="";
  });

  let cameraStream: MediaStream | null = null;
  let cameraRequest = 0;
  let removalObserver: MutationObserver | null = null;
  const stopCamera = () => {
    cameraRequest += 1;
    cameraStream?.getTracks().forEach((track) => track.stop());
    cameraStream=null; video.srcObject=null; cameraPanel.hidden=true; cameraStart.disabled=false;
    captured.hidden=true; retake.hidden=true; adopt.hidden=true; video.hidden=false; shutter.hidden=false;
    removalObserver?.disconnect(); removalObserver=null;
  };
  cameraStart.addEventListener("click", async () => {
    if (cameraStart.disabled) return;
    cameraStart.disabled=true; cameraPanel.hidden=false; cameraMessage.textContent="カメラを準備しています…";
    const request = ++cameraRequest;
    const getUserMedia = cameraOptions.getUserMedia ?? navigator.mediaDevices?.getUserMedia.bind(navigator.mediaDevices);
    if (getUserMedia === undefined) {
      cameraMessage.textContent="この環境ではカメラを起動できません。下の写真選択をお使いください。"; cameraStart.disabled=false; return;
    }
    try {
      /*
       * ★**解像度は `ideal` で要求する**（引き継ぎ §3.2）。既定のままだと端末が
       *   640×480 相当を返すことがあり、締めレシートの小さな数字が読めない。
       * ★`exact` にしない——出せない端末が**カメラごと起動できなくなる**。
       *   実際に読めるかは実機で判定する（この値は要求であって保証ではない）。
       */
      const stream = await getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1440 } }, audio: false });
      if (request !== cameraRequest || !section.isConnected) { stream.getTracks().forEach((track) => track.stop()); return; }
      cameraStream=stream; video.srcObject=stream; cameraMessage.textContent="伝票全体を画面に入れて撮影してください。";
      const Observer = document.defaultView?.MutationObserver ?? MutationObserver;
      removalObserver = new Observer(() => { if (!section.isConnected) stopCamera(); });
      removalObserver.observe(document.documentElement, { childList:true, subtree:true });
    } catch {
      if (request !== cameraRequest) return;
      cameraMessage.textContent="カメラを起動できませんでした。許可を確認するか、下の写真選択をお使いください。"; cameraStart.disabled=false;
    }
  });
  shutter.addEventListener("click", () => {
    const width=video.videoWidth; const height=video.videoHeight;
    if (width === 0 || height === 0) { cameraMessage.textContent="カメラ映像の準備ができていません。少し待ってください。"; return; }
    captured.width=width; captured.height=height; captured.getContext("2d")?.drawImage(video,0,0,width,height);
    captured.hidden=false; video.hidden=true; shutter.hidden=true; retake.hidden=false; adopt.hidden=false;
    cameraMessage.textContent="写真を確認し、撮り直すか使用してください。";
  });
  retake.addEventListener("click", () => {
    captured.hidden=true; video.hidden=false; shutter.hidden=false; retake.hidden=true; adopt.hidden=true;
    cameraMessage.textContent="伝票全体を画面に入れて撮影してください。";
  });
  adopt.addEventListener("click", () => {
    adopt.disabled=true;
    captured.toBlob((blob) => {
      adopt.disabled=false;
      if (blob === null) { cameraMessage.textContent="写真を作れませんでした。撮り直してください。"; return; }
      addDraft(new File([blob], `camera-${Date.now()}.jpg`, { type:"image/jpeg" }), type.value as DocumentType);
      retake.click(); cameraMessage.textContent="確認用画像を追加しました。続けて撮影できます。";
    }, "image/jpeg", 0.92);
  });
  closeCamera.addEventListener("click", stopCamera);
  document.defaultView?.addEventListener("pagehide", stopCamera, { once:true });

  const queue = node("section");
  /** まだ業務へ登録していない証憑。★並び順は一覧のまま（index を識別子にしない） */
  const pending = (items: readonly IntakeDocument[]): readonly IntakeDocument[] =>
    items.filter((item) => item.workflow_status !== "REGISTERED");
  /**
   * いま**確認の画面を開ける**証憑か。
   *
   * ★★**未登録であることと、開けることは別**（2026-09-20・通し検証で実測）。
   *   撮っただけ（`CAPTURED`）の伝票は、先に読取か手入力を通さないと候補が無く、
   *   編集の欄が出ない。もとは「次の未登録」をそのまま選んでいたため、
   *   **「登録して次へ」を押すと、選ばれたのに何も開かない**ことがあった。
   */
  const reviewable = (item: IntakeDocument): boolean =>
    item.workflow_status === "NEEDS_REVIEW" && item.ocr_draft !== null && item.ocr_draft !== undefined;
  const renderQueue = (items: readonly IntakeDocument[]) => {
    queue.replaceChildren(node("h2", "確認待ち"));
    /*
     * ★**残り件数は「保存済みの伝票のうち、まだ登録していないもの」**（引き継ぎ §8.2）。
     *   0件でも「紙がすべて撮影済み」「全業務完了」とは言えない——
     *   撮っていない紙のことは、この画面には何の情報も無い。
     */
    const left=pending(items).length;
    queue.append(node("p", left===0 ? "保存済みの伝票の確認待ちは0件です。" : `残り ${left}件`));
    if (items.length === 0) queue.append(node("p", "この日の伝票はまだありません。"));
    for (const doc of items) {
      const wrap=node("div"); wrap.className="document-review-row"; const row=node("button", `${LABELS[doc.doc_type]} ${STATUS[doc.workflow_status]}`); row.type="button";
      const preview=node("div"); preview.className="document-review-preview";
      row.addEventListener("click", async () => {
        /*
         * ★★**スマホでは、画像の置き場が既定で隠れている**（`data-mobile-pane="input"`）。
         *   ここを切り替えずに読み込むと、「画像を読み込んでいます…」から先が
         *   **何も起きないように見える**——実際には display:none の中へ描けていた（実機で実測）。
         * ★切替ボタンも一緒に出す。出さないと、開いた画像から入力へ戻れない。
         */
        // ★確認中の伝票は入力のまま（開いたときの自動表示でフォームを隠さない）
        if(selectedDocumentId!==doc.id){ wrap.dataset.mobilePane="image"; paneSwitch.hidden=false; }
        row.disabled=true; preview.replaceChildren(node("p", "画像を読み込んでいます…"));
        try {
          const image=await api.openImage(doc.id);
          const frame=node("div"); frame.className="document-image-frame";
          const shown=node("img"); shown.alt=`${LABELS[doc.doc_type]}の確認画像`; shown.src=`data:${image.mimeType};base64,${image.imageBase64}`;
          frame.append(shown);
          /*
           * ★**細かい字を読むための拡大は、ボタンとスクロールで足りる**（引き継ぎ §8.3）。
           *   画像編集ライブラリを足さない。滑らかなピンチ・パンは、実機で必要と分かってから。
           * ★はみ出しは**この枠の中だけ**にする。ページごと横に伸びると入力欄まで動く。
           * ★ブラウザ自身の拡大は禁止しない（`user-scalable=no` を足さない）。
           */
          let zoom=1;
          const apply=()=>{ shown.style.width=`${zoom*100}%`; fit.disabled=zoom===1; out.disabled=zoom<=1; };
          const controls=node("div"); controls.className="document-image-zoom";
          const into=node("button","拡大"); into.type="button"; into.addEventListener("click",()=>{ zoom=Math.min(4,zoom+0.5); apply(); });
          const out=node("button","縮小"); out.type="button"; out.addEventListener("click",()=>{ zoom=Math.max(1,zoom-0.5); apply(); });
          const fit=node("button","全体"); fit.type="button"; fit.addEventListener("click",()=>{ zoom=1; apply(); });
          controls.append(into,out,fit); apply();
          preview.replaceChildren(controls,frame);
        } catch {
          const error=node("p", "画像を開けませんでした。もう一度押してください。"); error.setAttribute("role","alert"); preview.replaceChildren(error);
        } finally { row.disabled=false; }
      });
      const actions=node("div"); actions.className="document-review-actions";
      const paneSwitch=node("div"); paneSwitch.className="document-pane-switch"; paneSwitch.hidden=selectedDocumentId!==doc.id;
      const showImage=node("button","画像を見る"); showImage.type="button"; const showInputs=node("button","入力を見る"); showInputs.type="button";
      showImage.addEventListener("click",()=>{wrap.dataset.mobilePane="image";if(preview.querySelector("img")===null)row.click();});
      showInputs.addEventListener("click",()=>{wrap.dataset.mobilePane="input";}); paneSwitch.append(showImage,showInputs);
      if (doc.workflow_status === "CAPTURED" || doc.workflow_status === "FAILED") {
        const read=node("button",doc.workflow_status === "FAILED" ? "読取を再試行" : "自動で読み取る"); read.type="button";
        const manual=node("button","手入力で確認"); manual.type="button";
        /**
         * 読取の開始・手入力への切り替え。
         *
         * ★★**失敗したときも一覧を取り直す**（2026-09-20・通し検証で実測）。
         *   読取が「未設定」で断られた時点で、サーバは**その証憑を `FAILED` にしている。**
         *   もとは失敗の文言を足すだけで一覧を取り直しておらず、
         *   **DB は `FAILED` なのに画面は「● 撮影済み」のまま**だった。
         *   状態を見て次の操作を決める画面なので、古い状態を残さない。
         * ★**文言は取り直したあとの行へ付け直す。** 消してしまうと、
         *   状態だけ変わって理由が消え、利用者は何が起きたか分からない。
         * ★編集中の値があるときは取り直さない（入力を捨てない。成功側と同じ判断）。
         */
        const act=async (run:()=>Promise<unknown>) => {
          read.disabled=true; manual.disabled=true;
          const notice=(text:string)=>{ const e=node("p",text); e.setAttribute("role","alert"); return e; };
          try {
            await run();
            const updated=await api.list();
            if(hasUnsavedDocumentDrafts()) actions.append(node("p","別の処理が完了しました。編集中の値を保存してから一覧を更新してください。"));
            else renderQueue(updated);
          } catch(error) {
            const message=error instanceof ApiError && error.code==="ocr_not_configured" ? "自動読取は未設定です。「手入力で確認」から続けてください。"
              : error instanceof ApiError && error.status===409 ? "別の処理が先に完了しました。値を残したまま、一覧を更新してください。"
              : "一時的に開始できませんでした。値は残っています。もう一度お試しください。";
            if(!hasUnsavedDocumentDrafts()){
              const updated=await api.list().catch(()=>null);
              if(updated!==null){
                renderQueue(updated);
                const host=queue.querySelector<HTMLElement>(`.document-review-row[data-document-id="${doc.id}"] .document-review-actions`);
                if(host!==null){ host.append(notice(message)); return; }
              }
            }
            actions.append(notice(message)); read.disabled=false; manual.disabled=false;
          }
        };
        read.addEventListener("click",()=>void act(()=>api.runOcr!(doc.id)));
        manual.addEventListener("click",()=>void act(()=>api.skipOcr!(doc.id)));
        actions.append(read,manual);
      }
      if (doc.workflow_status === "READING") actions.append(node("p","読取中です。完了までお待ちください。"));
      if (doc.workflow_status === "REGISTERED" && api.openRegistered !== undefined && doc.registered_target !== null && doc.registered_target !== undefined) {
        // ★間違いに気づくのは登録した後。ここから登録先の行まで行けるようにする（引き継ぎ §8.4）
        const open=node("button",`${TARGET_LABEL[doc.registered_target] ?? "登録先"}を見る`); open.type="button";
        open.addEventListener("click",()=>{ api.openRegistered!(doc.registered_target as string, doc.registered_target_id ?? null); });
        actions.append(open);
      }
      if (doc.workflow_status === "NEEDS_REVIEW" && doc.ocr_draft !== null && doc.ocr_draft !== undefined) {
        const open=node("button",selectedDocumentId===doc.id?"確認を閉じる":"画像と候補を確認・修正"); open.type="button"; open.disabled=editorStates.get(doc.id)?.saving===true; open.addEventListener("click",()=>{selectedDocumentId=selectedDocumentId===doc.id?null:doc.id;renderQueue(items);queue.querySelector<HTMLButtonElement>(`.document-review-row[data-document-id="${doc.id}"] > button`)?.click();}); actions.append(open);
      }
      if (doc.workflow_status === "NEEDS_REVIEW" && doc.ocr_draft !== null && doc.ocr_draft !== undefined && selectedDocumentId === doc.id) {
        const state=editorStates.get(doc.id) ?? {draft:{...doc.ocr_draft} as DocumentOcrDraft,version:doc.ocr_draft_version ?? 0,dirty:false,saving:false}; editorStates.set(doc.id,state);
        const draft=state.draft;
        const form=node("form"); form.className="document-review-form"; form.dataset.dirty=String(state.dirty);
        const common: Array<[keyof DocumentOcrDraft,string,"text"|"number"]> = [["documentDate","帳票の日付","text"],["vendorText","記載された取引先名（候補）","text"],["totalAmount",doc.doc_type==="POS_CLOSE"?"税込売上":"合計金額","number"]];
        const closing: Array<[keyof DocumentOcrDraft,string,"text"|"number"]> = [["settledAt","精算日時","text"],["cashSales","現金売上","number"],["cardSales","カード売上","number"],["otherSales","その他売上","number"],["cashBalance","現金残高（参考）","number"],["guestCount","客数","number"],["groupCount","組数","number"]];
        const fields=doc.doc_type==="POS_CLOSE"?[common[0]!,...closing,common[2]!]:common;
        const inputs:HTMLInputElement[]=[];
        for (const [key,labelText,inputType] of fields) { const label=node("label",labelText); const field=node("input"); inputs.push(field); field.value=draft[key] === null ? "" : String(draft[key]);
          // ★帳票の日付は日付の欄にする。★読取候補が YYYY-MM-DD 以外なら文字のまま（日付の欄に入れると値が黙って消える）
          field.type=key==="documentDate" && /^(\d{4}-\d{2}-\d{2})?$/.test(field.value) ? "date" : inputType; field.maxLength=key==="vendorText"?120:25; field.disabled=state.saving; field.addEventListener("input",()=>{ state.dirty=true; form.dataset.dirty="true"; (draft as unknown as Record<string,string|number|null>)[key]=field.value===""?null:inputType==="number"?Number(field.value):field.value; }); label.append(field); form.append(label); }
        const save=node("button","確認下書きを保存"); save.type="submit"; const result=node("p");
        /*
         * ★**競合したら、最新の値を見られるようにする**（引き継ぎ §4「競合」）。
         *   もとは「内容を控えて一覧を更新してください」だけで、**控える先が画面に無かった。**
         *   ★手元の入力は消さない。並べて見せて、どちらを残すかは人が決める。
         */
        const conflict=node("div"); conflict.hidden=true;
        const showLatest=node("button","相手の最新値を見る"); showLatest.type="button";
        const latest=node("dl");
        showLatest.addEventListener("click",async()=>{
          showLatest.disabled=true;
          try{
            const current=(await api.list()).find((item)=>item.id===doc.id);
            const serverDraft=current?.ocr_draft;
            latest.replaceChildren();
            if(serverDraft===null||serverDraft===undefined){latest.append(node("p","相手の読取候補を取得できませんでした。"));}
            else{for(const [key,labelText] of fields){const value=(serverDraft as unknown as Record<string,unknown>)[key];latest.append(node("dt",labelText),node("dd",value===null||value===undefined?"未読取":String(value)));}}
          }catch{latest.replaceChildren(node("p","最新の値を取得できませんでした。通信を確認してもう一度押してください。"));}
          finally{showLatest.disabled=false;}
        });
        conflict.append(showLatest,latest);
        form.addEventListener("submit",async(e)=>{e.preventDefault();state.saving=true;save.disabled=true;inputs.forEach((field)=>{field.disabled=true;});try{const saved=await api.saveDraft!(doc.id,state.version,draft);state.version=saved.version;state.dirty=false;form.dataset.dirty="false";result.textContent="✓ 下書きを保存しました";conflict.hidden=true;}catch(error){state.dirty=true;form.dataset.dirty="true";const collided=error instanceof ApiError && error.status===400;conflict.hidden=!collided;result.textContent=collided?"別の端末で更新されています。入力値は残しています。相手の最新値を見てから、残す内容を決めてください。":"一時的に保存できませんでした。入力値は残っています。もう一度保存してください。";}finally{state.saving=false;save.disabled=false;inputs.forEach((field)=>{field.disabled=false;});}});
        form.append(save,result,conflict);
        if(api.register!==undefined&&api.registrationOptions!==undefined){
          /*
           * ★**登録先の選択は `prompt` を使わない**（引き継ぎ §3.3「IDやCASH等を入力させる prompt は禁止」）。
           *   もとは業者ID・科目ID・`CASH` を利用者に**打たせて**いた。打ち間違えれば
           *   別の業者へ登録され、`prompt` はスマホで長い一覧を出せない。
           * ★**無い業者・科目をここで作らない。** 空のときは登録させず、マスタ登録へ誘導する。
           */
          const registerBox=node("div");registerBox.className="document-register";
          const fields=node("div");fields.className="document-register-fields";
          // ★次の伝票があるなら、それをボタンの文言で先に知らせる（引き継ぎ §8.2）。
          //   ★**開ける伝票だけを数える**——撮っただけの伝票は「次へ」で開けない
          const hasNext=pending(items).some((item)=>item.id!==doc.id&&reviewable(item));
          const register=node("button",hasNext?"登録して次へ":"業務へ登録");register.type="button";
          const registrationMessage=node("p");registrationMessage.setAttribute("role","status");
          const needsMaster=doc.doc_type==="DELIVERY_NOTE"||doc.doc_type==="RECEIPT";
          let readPayload:()=>Record<string,unknown>=()=>({});
          if(doc.doc_type==="POS_CLOSE"){
            readPayload=()=>({salesInclTaxDinner:draft.totalAmount,guestsDinner:draft.guestCount,groupsDinner:draft.groupCount,cashAmount:draft.cashSales,cardAmount:draft.cardSales,otherAmount:draft.otherSales});
          }
          if(doc.doc_type==="DELIVERY_NOTE"&&!cameraOptions.canApprovePurchase){register.disabled=true;registrationMessage.textContent="仕入の確定登録は店長またはオーナーが行います。";}
          else if(needsMaster){
            register.disabled=true;registrationMessage.textContent="登録先を読み込んでいます…";
            void (async()=>{
              try{
                const options=await api.registrationOptions!();
                if(doc.doc_type==="DELIVERY_NOTE"){
                  if(options.vendors.length===0){registrationMessage.textContent="業者が登録されていません。先に「業者・科目」で登録してください。";return;}
                  const vendor=labeledSelect("取引先",options.vendors.map((v)=>({value:v.id,label:v.name})));
                  const category=labeledSelect("区分",Object.keys(CATEGORY_LABEL).map((value)=>({value,label:CATEGORY_LABEL[value] ?? value})));
                  // ★区分は業者の既定を入れておくが、**利用者が確認・変更できる**（引き継ぎ §3.4）
                  const syncCategory=()=>{const chosen=options.vendors.find((v)=>v.id===vendor.select.value)?.category;if(chosen!==undefined&&chosen in CATEGORY_LABEL)category.select.value=chosen;};
                  syncCategory();vendor.select.addEventListener("change",syncCategory);
                  fields.append(vendor.label,category.label);
                  readPayload=()=>({vendorId:vendor.select.value,category:category.select.value,amount:draft.totalAmount});
                }else{
                  if(options.accounts.length===0){registrationMessage.textContent="小口の科目が登録されていません。先に「業者・科目」で登録してください。";return;}
                  const account=labeledSelect("科目",options.accounts.map((a)=>({value:a.id,label:a.name})));
                  const method=labeledSelect("支払方法",Object.keys(METHOD_LABEL).map((value)=>({value,label:METHOD_LABEL[value] ?? value})));
                  fields.append(account.label,method.label);
                  readPayload=()=>({accountId:account.select.value,paymentMethod:method.select.value,amount:draft.totalAmount});
                }
                register.disabled=false;registrationMessage.textContent="";
              }catch{registrationMessage.textContent="登録先の一覧を取得できませんでした。もう一度開いてください。";}
            })();
          }
          register.addEventListener("click",async()=>{
            if(state.dirty||state.saving){registrationMessage.textContent="先に確認下書きを保存してください。";return;}
            register.disabled=true;
            try{
              const payload:Record<string,unknown>={version:state.version,...readPayload()};
              // ★未読取（null）と 0 を混同しない。0 を送れるのは利用者が 0 と確認した欄だけ
              if(Object.values(payload).some((value)=>value===null||value===""))throw new Error("読み取れていない項目があります。入力してから登録してください。");
              const kind=doc.doc_type==="DELIVERY_NOTE"?"purchase":doc.doc_type==="RECEIPT"?"petty-cash":"sales";
              await api.register!(doc.id,kind,payload);
              editorStates.delete(doc.id);registrationMessage.textContent="✓ 業務へ登録しました";
              /*
               * ★**登録が成功してから、次の未登録の伝票を開く**（引き継ぎ §8.2）。
               *   失敗した場合はここへ来ないので、値も選択もその場に残る。
               * ★一覧を取り直してから選ぶ——別の端末で処理済みになった伝票を飛ばすため。
               * ★次が無ければ何も開かない（件数の行が「0件です」に変わる）。
               */
              const updated=await api.list();
              selectedDocumentId=pending(updated).find((item)=>item.id!==doc.id&&reviewable(item))?.id ?? null;
              renderQueue(updated);
              /*
               * ★開ける伝票が無いのに未登録が残っているなら、**何が要るかを言う。**
               *   黙って一覧へ戻すと、残り件数だけ見えて次の操作が分からない。
               */
              const left=pending(updated).filter((item)=>item.id!==doc.id);
              if(selectedDocumentId===null&&left.length>0){
                const e=node("p",`残り ${left.length}件は、先に「自動で読み取る」か「手入力で確認」を通してください。`);
                e.setAttribute("role","status"); queue.append(e);
              }
            }catch(error){registrationMessage.textContent=error instanceof Error?error.message:"登録できませんでした";register.disabled=false;}
          });
          registerBox.append(fields,register,registrationMessage);form.append(registerBox);
        }
        actions.append(form);
      }
      wrap.dataset.mobilePane="input"; wrap.dataset.documentId=doc.id; wrap.append(row,paneSwitch,preview,actions); queue.append(wrap);
    }
  };
  renderQueue(documents);
  section.append(queue, node("p", "画像は入力補助・確認用です。原本は紙で保管してください。"));
  return section;
}
